import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

const SidebarDropdown = ({ 
  children, 
  isOpen, 
  toggleDropdown, 
  additionalclasses, 
  position = 'bottom',
  triggerRef 
}) => {
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);

  const calculatePosition = () => {
    if (!triggerRef.current || !dropdownRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - dropdownRect.height - 8;
        left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2);
        break;
      case 'bottom':
        top = triggerRect.bottom + 0;
        left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2);
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height / 2) - (dropdownRect.height / 2);
        left = triggerRect.left - dropdownRect.width - 8;
        break;
      case 'right':
        top = triggerRect.top;
        left = triggerRect.right + 8;
        break;
      default:
        break;
    }

    // Adjust for window boundaries
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Horizontal boundary check
    if (left < 0) left = 8;
    if (left + dropdownRect.width > windowWidth) {
      left = windowWidth - dropdownRect.width - 8;
    }

    // Vertical boundary check
    if (top < 0) top = 8;
    if (top + dropdownRect.height > windowHeight) {
      top = windowHeight - dropdownRect.height - 8;
    }

    return { 
      top: top + window.scrollY, 
      left: left + window.scrollX 
    };
  };

  const updatePosition = () => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      setDropdownPosition(calculatePosition());
      setIsPositionCalculated(true);
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && triggerRef.current) {
      const isClickInsideDropdown = dropdownRef.current.contains(event.target);
      const isClickInsideTrigger = triggerRef.current.contains(event.target);
      
      if (!isClickInsideDropdown && !isClickInsideTrigger) {
        toggleDropdown();
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      
      const handleUpdate = () => {
        updatePosition();
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleUpdate);
      window.addEventListener('resize', handleUpdate);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      ref={dropdownRef}
      className={`fixed z-[99999] min-w-[200px] lg:min-w-[284px] bg-white-50 dark:bg-grey-600 rounded-[8px] custom-border-grey-300 custom-shadow grid content-start gap-[4px] p-[4px] ignore-click ${additionalclasses}`}
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        visibility: isPositionCalculated ? 'visible' : 'hidden'
      }}
    >
      {children}
    </div>,
    document.body
  );
};

SidebarDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleDropdown: PropTypes.func.isRequired,
  additionalclasses: PropTypes.string,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  triggerRef: PropTypes.shape({ 
  // current: PropTypes.oneOfType([
  //   PropTypes.instanceOf(typeof window !== 'undefined' ? window.HTMLElement : Object),
  //   PropTypes.null
  // ])
}).isRequired
};

SidebarDropdown.defaultProps = {
  additionalclasses: '',
  position: 'bottom'
};

export default SidebarDropdown;